import React, { useState } from "react";
import {
  bandFor,
  computeScores,
  itemWeight,
  scaleLabels,
} from "../templateUtils";

const range = (min, max) => {
  const out = [];
  if (
    Number.isInteger(min) &&
    Number.isInteger(max) &&
    max >= min &&
    max - min <= 20
  )
    for (let v = min; v <= max; v++) out.push(v);
  return out;
};

const sampleRating = (section) =>
  Math.ceil((Number(section.scale.min) + Number(section.scale.max)) / 2);

const RatingPreview = ({ section, ratings, setRating }) => {
  const min = Number(section.scale.min);
  const max = Number(section.scale.max);
  const values = range(min, max);
  const labelOf = new Map(
    scaleLabels(section.scale).map((l) => [l.value, l.label]),
  );
  return (
    <>
      {section.items.map((item) => {
        const value = ratings[item._id] ?? sampleRating(section);
        return (
          <div key={item._id} className="preview-item">
            <div className="d-flex justify-content-between gap-2">
              <div className="fw-semibold fs-085">
                {item.title || (
                  <span className="text-slate-400">Untitled item</span>
                )}
              </div>
              <span className="text-slate-500 fs-070 text-nowrap">
                {Math.round(itemWeight(section, item) * 100) / 100}%
              </span>
            </div>
            {item.description && (
              <div className="text-slate-500 fs-075">{item.description}</div>
            )}
            {item.kpis.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mt-1">
                {item.kpis.map((k) => (
                  <span key={k} className="chip chip--sm">
                    {k}
                  </span>
                ))}
              </div>
            )}
            <div
              className="preview-scale"
              role="radiogroup"
              aria-label={`Sample rating for ${item.title || "item"}`}
            >
              {values.map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={value === v}
                  className={`preview-scale-btn ${value === v ? "active" : ""}`}
                  title={labelOf.get(v) || String(v)}
                  onClick={() => setRating(item._id, v)}
                >
                  {v}
                </button>
              ))}
              {labelOf.get(value) && (
                <span className="text-slate-500 fs-070 ms-1">
                  {labelOf.get(value)}
                </span>
              )}
            </div>
            {section.allowItemComments && (
              <div className="preview-field">Critical incidents…</div>
            )}
          </div>
        );
      })}
      {section.allowSectionComment && (
        <div className="preview-field mt-2">Section critical incidents…</div>
      )}
    </>
  );
};

/**
 * The form as the evaluator will see it, with clickable sample ratings and
 * the computed total + band (same formula as the server).
 */
const TemplatePreview = ({ form }) => {
  const [ratings, setRatings] = useState({}); // { [section._id]: { [item._id]: value } }

  // Sample ratings default to the middle of each scale
  const effective = Object.fromEntries(
    form.sections
      .filter((s) => s.type === "RATING")
      .map((s) => [
        s._id,
        Object.fromEntries(
          s.items.map((it) => [
            it._id,
            ratings[s._id]?.[it._id] ?? sampleRating(s),
          ]),
        ),
      ]),
  );
  const scores = computeScores(form.sections, effective);
  const band = bandFor(form.ratingBands, scores.total);
  const setRating = (sectionId) => (itemId, value) =>
    setRatings((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], [itemId]: value },
    }));

  return (
    <aside className="builder-preview" aria-label="Live preview">
      <div className="builder-preview-head">
        <span className="fw-bold text-navy">Live preview</span>
        <button
          type="button"
          className="btn btn-sm btn-link p-0 text-slate-600"
          onClick={() => setRatings({})}
        >
          Reset samples
        </button>
      </div>

      <div className="builder-preview-body">
        <div className="fw-bold text-navy">
          {form.name || "Untitled template"}
        </div>
        <div className="text-slate-500 fs-075 mb-3">
          {form.code || "CODE"} · {form.evaluationType}
        </div>

        {form.sections.length === 0 && (
          <p className="text-slate-400 fs-080">
            Sections appear here as you add them.
          </p>
        )}

        {form.sections.map((s) => (
          <div key={s._id} className="preview-section">
            <div className="d-flex justify-content-between gap-2">
              <div className="preview-section-title">
                {s.title || "Untitled section"}
              </div>
              {s.type === "RATING" && (
                <span className="section-weight-chip">{s.weight || 0}%</span>
              )}
            </div>
            {s.description && (
              <div className="text-slate-500 fs-075 mb-1">{s.description}</div>
            )}

            {s.type === "RATING" && (
              <RatingPreview
                section={s}
                ratings={effective[s._id] || {}}
                setRating={setRating(s._id)}
              />
            )}
            {s.type === "TEXT_LIST" &&
              Array.from(
                { length: Math.min(Number(s.maxEntries) || 3, 5) },
                (_, i) => (
                  <div key={i} className="preview-field">
                    {i + 1}.
                  </div>
                ),
              )}
            {s.type === "TABLE" && (
              <div className="table-responsive">
                <table className="table table-sm table-bordered preview-table mb-0">
                  <thead>
                    <tr>
                      {s.columns.map((c) => (
                        <th key={c._id}>{c.label || "Column"}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {s.columns.map((c) => (
                        <td key={c._id} className="text-slate-400">
                          {c.type === "date" ? "mm/dd/yyyy" : ""}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
                {s.maxRows !== "" && (
                  <div className="text-slate-400 fs-070 mt-1">
                    Up to {s.maxRows} rows
                  </div>
                )}
              </div>
            )}
            {s.type === "TEXT" && (
              <div className="preview-field preview-field--tall"></div>
            )}
          </div>
        ))}
      </div>

      {scores.sections.length > 0 && (
        <div className="builder-preview-score">
          {scores.sections.map((r) => (
            <div
              key={r.id}
              className="d-flex justify-content-between fs-075 text-slate-600"
            >
              <span className="text-truncate me-2">{r.title}</span>
              <span className="tabular-nums text-nowrap">
                {r.achieved}% × {r.weight}% = <strong>{r.score}</strong>
              </span>
            </div>
          ))}
          <div className="d-flex justify-content-between align-items-end mt-2">
            <div>
              <div className="approval-meta-label">Total</div>
              <div className="preview-total tabular-nums">{scores.total}</div>
            </div>
            <span
              className={`preview-band ${band ? "" : "preview-band--none"}`}
            >
              {band || "No band covers this score"}
            </span>
          </div>
        </div>
      )}
    </aside>
  );
};

export default TemplatePreview;
